import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {RadioField} from '../radio-field/RadioField'
import {Text} from '@/components/ui/text/Text'
import {TKey, useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {Pencil01Icon} from '@/components/ui/icon/Icon'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {ValidationSchema, type Entity, type GetExcludedEntityColumns} from './ExportModal'
import {CustomUserColumns} from '@/features/users/types'
import {
    Dialog,
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {useState} from 'react'
import {ObjectKeys} from '@/utilities/helpers'
import {Checkbox} from '../checkbox/ChekBox'
import {columnNameToLabel as usersColumnNameToLabel} from '@/features/users/utils'
import {columnNameToLabel as patientsColumnNameToLabel} from '@/features/patients/utils'
import {columnNameToLabel as projectsColumnNameToLabel} from '@/features/projects/utils'
import {PresetProjectColumns} from '@/features/projects/types'

type ColumnsLabelMap<E extends Entity> = Record<GetExcludedEntityColumns<E>, TKey>

const getColumnsNameToLabel = <E extends Entity>(entity: E): ColumnsLabelMap<E> => {
    switch (entity) {
        case 'users':
            return {
                ...usersColumnNameToLabel,
                [CustomUserColumns.enum.taxCode]: 'users:user_column_tax_code',
                [CustomUserColumns.enum.patientAgeRange]: 'users:user_column_patient_age_range',
                [CustomUserColumns.enum.startDate]: 'users:user_column_start_date',
                [CustomUserColumns.enum.interruptionDate]: 'users:interruption_date',
                [CustomUserColumns.enum.registrationDate]: 'users:user_column_registration_date',
                [CustomUserColumns.enum.fm08Evaluation]: 'users:user_column_fm08_evaluation',
                [CustomUserColumns.enum.fm08EvaluationLastUpdate]: 'users:user_column_fm08_evaluation_last_update',
                [CustomUserColumns.enum.curriculumLastUpdate]: 'users:user_column_curriculum_last_update',
                [CustomUserColumns.enum.fm68]: 'users:user_column_fm68',
                [CustomUserColumns.enum.privacy]: 'users:user_column_privacy',
                [CustomUserColumns.enum.professionalInsuranceLastVerify]:
                    'users:user_column_professional_insurance_last_verify',
                [CustomUserColumns.enum.workSuitableUpTo]: 'users:user_column_work_suitable_up_to',
                [CustomUserColumns.enum.threeYearsEcm]: 'users:user_column_three_years_ecm',
                [CustomUserColumns.enum.residence]: 'users:user_column_residence',
                [CustomUserColumns.enum.domicile]: 'users:user_column_domicile'
            } as ColumnsLabelMap<E>
        case 'patients':
            return patientsColumnNameToLabel as ColumnsLabelMap<E>
        case 'projects':
            return {
                ...projectsColumnNameToLabel,
                [PresetProjectColumns.enum.assistiveRegime]: 'projects:project_column_assistive_regime',
                [PresetProjectColumns.enum.residenceAsl]: 'projects:project_column_residence_asl',
                [PresetProjectColumns.enum.residenceAslDistrict]: 'projects:project_column_residence_asl_district',
                [PresetProjectColumns.enum.domicileAsl]: 'projects:project_column_domicile_asl',
                [PresetProjectColumns.enum.domicileAslDistrict]: 'projects:project_column_domicile_asl_district',
                [PresetProjectColumns.enum.equipe]: 'projects:project_column_equipe',
                [PresetProjectColumns.enum.plannedWeeklyTreatments]:
                    'projects:project_column_planned_weekly_treatments',
                [PresetProjectColumns.enum.plannedMonthlyTreatments]:
                    'projects:project_column_planned_monthly_treatments',
                [PresetProjectColumns.enum.otherDiagnosisCode]: 'projects:project_column_other_diagnosis_code'
            } as ColumnsLabelMap<E>
        default:
            throw new Error(`Unknown entity: ${entity}`)
    }
}

const getPresetColumns = <E extends Entity>(entity: E) => {
    switch (entity) {
        case 'users':
            return CustomUserColumns.options
        case 'patients':
            return []
        case 'projects':
            return PresetProjectColumns.options
        default:
            throw new Error(`Unknown entity: ${entity}`)
    }
}

export const ExportModalCustomColumns = <E extends Entity>({entity}: {entity: E}) => {
    const {t} = useTranslation()
    const form = useFormContext<ValidationSchema<E>>()
    const selectedMode = useWatch({control: form.control, name: 'mode'})
    const customSelectedColumns = useWatch({control: form.control, name: 'customColumns'})

    const columnsLabelMap = getColumnsNameToLabel(entity)

    return (
        <Flexbox gap={2} justify="space-between" align="center" fullWidth>
            <RadioField
                label={
                    <Flexbox direction="column">
                        <Text size="textSm" palette="neutral" shade="950">
                            {t('commons:custom_columns')}
                        </Text>
                        {selectedMode == 'custom' && (
                            <Text size="textSm" palette="neutral" shade="600">
                                {!customSelectedColumns || customSelectedColumns.length == 0
                                    ? t('commons:select_a_column')
                                    : t('commons:custom_columns_selected', {count: customSelectedColumns?.length ?? 0})}
                            </Text>
                        )}
                    </Flexbox>
                }
                value="custom"
                {...form.register('mode')}
            />
            {selectedMode == 'custom' && (
                <DialogProvider>
                    <DialogDisclosure
                        render={
                            <Button variant="secondary" size="sm" style={{flexShrink: 0}}>
                                <Pencil01Icon />
                                {t('commons:edit')}
                            </Button>
                        }
                    />
                    <Dialog width="450px">
                        <Controller
                            control={form.control}
                            name="customColumns"
                            render={({field}) => (
                                <Content<E>
                                    entity={entity}
                                    columnsLabelMap={columnsLabelMap}
                                    selectedColumns={field.value ?? []}
                                    onSubmit={field.onChange}
                                />
                            )}
                        />
                    </Dialog>
                </DialogProvider>
            )}
        </Flexbox>
    )
}

const Content = <E extends Entity>({
    entity,
    columnsLabelMap,
    selectedColumns,
    onSubmit
}: {
    entity: E
    columnsLabelMap: ColumnsLabelMap<E>
    selectedColumns: GetExcludedEntityColumns<E>[]
    onSubmit: (columns: GetExcludedEntityColumns<E>[]) => void
}) => {
    const {t} = useTranslation()
    const [localColumns, setLocalColumns] = useState<GetExcludedEntityColumns<E>[]>(selectedColumns)

    const columnKeys = ObjectKeys(columnsLabelMap)
    const presetColumns = getPresetColumns(entity)
    const presetColumnKeys = columnKeys.filter(col => presetColumns.some(presetCol => presetCol === col))
    const isPresetFullySelected =
        presetColumnKeys.length > 0 && presetColumnKeys.every(col => localColumns.includes(col))

    const columnHeading = {
        users: t('commons:personal'),
        patients: t('commons:patients'),
        projects: t('commons:projects', {count: 2})
    } as const satisfies Record<Entity, string>

    const handlePresetToggle = () => {
        if (isPresetFullySelected) {
            setLocalColumns(prevState => prevState.filter(col => !presetColumnKeys.includes(col)))
        } else {
            setLocalColumns(prevState => {
                const newColumns: GetExcludedEntityColumns<E>[] = [...prevState]
                presetColumnKeys.forEach(column => {
                    if (!newColumns.includes(column)) {
                        newColumns.push(column)
                    }
                })
                return newColumns
            })
        }
    }

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('commons:table_columns')}</DialogHeading>
            </DialogHeader>
            <ScrollArea style={{maxHeight: 500}} scrollbar={<Scrollbar />}>
                <DialogBody>
                    <Flexbox direction="column" gap={3}>
                        <Text render="h3" size="textMd" palette="neutral" shade="600">
                            {columnHeading[entity]}
                        </Text>

                        {presetColumnKeys.length > 0 && (
                            <Checkbox
                                id="select-preset"
                                label={t('commons:select_preset_columns')}
                                checked={isPresetFullySelected}
                                onChange={handlePresetToggle}
                            />
                        )}

                        <Checkbox
                            id="export-modal-select-all"
                            label={t('commons:select_all')}
                            checked={localColumns.length === columnKeys.length && columnKeys.length > 0}
                            onChange={() =>
                                localColumns.length === columnKeys.length
                                    ? setLocalColumns([])
                                    : setLocalColumns([...columnKeys])
                            }
                        />

                        {columnKeys.map(column => (
                            <Flexbox key={column} align="center" gap={2}>
                                <Checkbox
                                    id={column}
                                    label={t(columnsLabelMap[column])}
                                    checked={localColumns.includes(column)}
                                    onChange={() =>
                                        setLocalColumns(prevState =>
                                            prevState.includes(column)
                                                ? prevState.filter(c => c !== column)
                                                : [...prevState, column]
                                        )
                                    }
                                />
                            </Flexbox>
                        ))}
                    </Flexbox>
                </DialogBody>
            </ScrollArea>
            <DialogFooter style={{justifyContent: 'flex-end', gap: 8}}>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>}></DialogDismiss>
                <DialogDismiss
                    render={
                        <Button variant="primaryNeutral" onClick={() => onSubmit(localColumns)}>
                            {t('commons:confirm')}
                        </Button>
                    }
                ></DialogDismiss>
            </DialogFooter>
        </>
    )
}
